import { useCallback, useMemo } from 'react';

import type { ExtendedSeriesOptionsType } from '@/components/Chart/types/highcharts';
import { getSeriesId } from '@/components/Chart/utils/seriesVisibility';

import type { SelectChangeEvent } from '@mui/material';

const useLegendDropdown = ({
  series,
  hiddenSeriesIds,
  setHiddenSeriesIds
}: {
  series: ExtendedSeriesOptionsType[];
  hiddenSeriesIds: string[];
  setHiddenSeriesIds: (ids: string[]) => void;
}) => {
  const allChartSeries = useMemo(
    () => series.filter(item => !item.name?.includes('Navigator')),
    [series]
  );
  const allSeriesIds = useMemo(
    () => allChartSeries.map(item => getSeriesId(item)).filter((id): id is string => Boolean(id)),
    [allChartSeries]
  );
  const selectedSeriesIds = useMemo(() => {
    const visibleIds = allSeriesIds.filter(id => !hiddenSeriesIds.includes(id));
    return visibleIds.length > 0 ? visibleIds : allSeriesIds;
  }, [allSeriesIds, hiddenSeriesIds]);
  const selectedSeries = useMemo(
    () => allChartSeries.filter(item => selectedSeriesIds.includes(getSeriesId(item) ?? '')),
    [allChartSeries, selectedSeriesIds]
  );

  const handleLegendItemClick = useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const value = e.target.value;
      const nextSelectedIds = typeof value === 'string' ? value.split(',') : value;
      if (!nextSelectedIds.length) return;
      const nextHiddenIds = allSeriesIds.filter(id => !nextSelectedIds.includes(id));
      setHiddenSeriesIds(nextHiddenIds);
    },
    [allSeriesIds, setHiddenSeriesIds]
  );

  const isSeriesDisabled = useCallback(
    (item: ExtendedSeriesOptionsType) => {
      const seriesId = getSeriesId(item);
      return selectedSeriesIds.length === 1 && seriesId === selectedSeriesIds[0];
    },
    [selectedSeriesIds]
  );

  return {
    selectedSeries,
    selectedSeriesIds,
    allChartSeries,
    handleLegendItemClick,
    isSeriesDisabled
  };
};

export default useLegendDropdown;
